import type { GenerateSignedUploadUrlInput } from '~/types/graphql-backend-types/gql-types'
import { useMutation } from '@tanstack/vue-query'
import gql from 'graphql-tag'

export interface UploadDocumentPayload {
  file: File
  fileName?: string
}

export function useUploadDocument() {
  return useMutation({
    mutationKey: ['upload-document'],
    mutationFn: (payload: UploadDocumentPayload) => uploadDocument(payload),
  })
}

async function uploadDocument(payload: UploadDocumentPayload): Promise<string> {
  const { file } = payload

  const generateFileUrl: GenerateSignedUploadUrlInput = {
    fileName: file.name,
    contentType: file.type,
  }

  // Generate signed upload url
  const signedUploadUrl = await generateSignedUploadUrl(generateFileUrl)

  // Upload file
  await uploadFileToSignedUrl(file, signedUploadUrl.url)

  return signedUploadUrl.url
}

async function uploadFileToSignedUrl(file: File, signedUrl: string) {
  try {
    const response = await fetch(signedUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': 'multipart/form-data' },
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    return response
  }
  catch (error) {
    console.error('Error uploading file:', error)
    throw error
  }
}

async function generateSignedUploadUrl(input: GenerateSignedUploadUrlInput) {
  const { mutate } = useGqlMikro()

  const { data, errors } = await mutate({
    mutation: GENERATE_SIGNED_UPLOAD_URL,
    variables: { input },
  })

  if (errors && errors.length > 0) {
    throw errors
  }

  return data!.generateSignedUploadUrl
}

const GENERATE_SIGNED_UPLOAD_URL = gql`
mutation GenerateSignedUploadUrl($input: GenerateSignedUploadUrlInput!) {
  generateSignedUploadUrl(input: $input) {
    url
  }
}
`
